import { Children, isValidElement, type ReactNode } from "react";

/** Gap size tokens — variants rather than precise values so spacing stays consistent. */
type GridGap = "sm" | "md" | "lg" | "xl";

const gapXClass: Record<GridGap, string> = {
  sm: "gap-x-4",
  md: "gap-x-8",
  lg: "gap-x-12",
  xl: "gap-x-16",
};

const gapYClass: Record<GridGap, string> = {
  sm: "gap-y-4",
  md: "gap-y-8",
  lg: "gap-y-12",
  xl: "gap-y-16",
};

/**
 * The px width of each gap token at the default 16px root font size, so the
 * grid can do arithmetic with it. The classes themselves are rem.
 */
const gapPx: Record<GridGap, number> = { sm: 16, md: 32, lg: 48, xl: 64 };
const DEFAULT_GAP_PX = 16;

/** A px length at the default root font size, as rem. */
function rem(px: number): string {
  return `${px / 16}rem`;
}

interface GridProps {
  // Required — no default. Every Grid usage should make a conscious
  // decision about how many columns it needs.
  //
  // A number is that many equal columns. An array is *unequal* columns whose
  // widths follow the ratio — `[1, 2]` is a narrow column beside one twice as
  // wide, for a layout where one side (a stepper, a couple of inputs) needs far
  // less room than the other (a row of dimension fields). Equal columns would
  // force the small side to match the big one and waste the width.
  columns: number | number[];
  /**
   * The narrowest a column may get. Set it and `columns` becomes a maximum
   * rather than a fixed count: the grid drops columns — to one if it must —
   * once they'd be squeezed below this. Without it the count never changes and
   * a narrow screen squashes the columns until their content spills.
   *
   * Give it the width the column's content actually needs, not a breakpoint.
   * This is container-driven, so it does the right thing regardless of the
   * viewport — which matters when the same grid sits in panels of different
   * widths.
   *
   * With unequal columns (`columns` as an array) it's the *smallest* column's
   * floor — the narrowest track is the one that runs out of room first, so
   * that's the one worth watching. Below the width that would squeeze it under
   * this, the grid drops to a single column. Set it to what that column's
   * content needs (a field's placeholder, say), or that content clips on a
   * narrow screen. Here it's read as rem (px at the default 16px root), so it
   * grows with the text whenever the browser renders text larger.
   */
  minColumnWidth?: number | undefined;
  /** Gap on both axes. Overridden per-axis by `gapX` / `gapY`. */
  gap?: GridGap;
  /** Column gap (horizontal). Falls back to `gap`. */
  gapX?: GridGap;
  /**
   * Row gap (vertical). Falls back to `gap`. Unequal columns that have
   * collapsed to one cap it at `md` — stacked fields don't need a wide gutter.
   */
  gapY?: GridGap;
  children: ReactNode;
}

/**
 * `repeat(auto-fit, ...)` alone would also *add* columns whenever there's room,
 * blowing past `columns`. Flooring each track at the exact 1/columns share
 * stops that: while every column can have its share, the share wins and you get
 * exactly `columns` of them; once the share drops under `minColumnWidth`, that
 * wins instead and auto-fit packs in however many still fit. The outer `min(100%)`
 * keeps a single column from overflowing a container narrower than
 * `minColumnWidth`.
 */
function autoFitColumns(
  columns: number,
  minColumnWidth: number,
  columnGapPx: number,
): string {
  const share = `calc((100% - ${(columns - 1) * columnGapPx}px) / ${columns})`;
  const track = `min(100%, max(${minColumnWidth}px, ${share}))`;
  return `repeat(auto-fit, minmax(${track}, 1fr))`;
}

export function Grid({
  columns,
  minColumnWidth,
  gap,
  gapX,
  gapY,
  children,
}: GridProps) {
  const x = gapX ?? gap;
  const y = gapY ?? gap;
  // No gap props → the original `gap-4` default (keeps existing call sites
  // byte-identical). Any gap prop → explicit per-axis classes.
  const gapClasses =
    x || y ? `${gapXClass[x ?? "sm"]} ${gapYClass[y ?? "sm"]}` : "gap-4";

  if (Array.isArray(columns)) {
    return (
      <AsymmetricGrid_
        ratios={columns}
        {...(minColumnWidth !== undefined && { minColumnWidth })}
        gapX={x ?? "sm"}
        gapY={y ?? "sm"}
      >
        {children}
      </AsymmetricGrid_>
    );
  }

  const gridTemplateColumns =
    minColumnWidth === undefined
      ? `repeat(${columns}, minmax(0, 1fr))`
      : autoFitColumns(columns, minColumnWidth, x ? gapPx[x] : DEFAULT_GAP_PX);

  return (
    <div className={`grid ${gapClasses}`} style={{ gridTemplateColumns }}>
      {children}
    </div>
  );
}

/**
 * Unequal columns that still collapse to one. `repeat(auto-fit)` can't do
 * unequal tracks, so this is a wrapping flex row: each child gets its column's
 * share of the width, so exactly one row's worth fits per line.
 *
 * The switch to one column is plain arithmetic in `flex-basis`, deliberately
 * not a container query, which appears to misjudge the container's width under
 * Safari's browser zoom and collapse a grid that has room. Arithmetic stays in
 * the layout's own units at every zoom level.
 *
 * Every length in it is rem, like Tailwind's gaps and the fields' widths. At
 * deep Safari zoom (around 50%) text stops shrinking and rem appears to grow
 * with it, so content needs more room than its px size says. A rem threshold
 * grows too, and the grid stacks instead of squeezing that content.
 */
function AsymmetricGrid_({
  ratios,
  minColumnWidth,
  gapX,
  gapY,
  children,
}: {
  ratios: number[];
  minColumnWidth?: number | undefined;
  gapX: GridGap;
  gapY: GridGap;
  children: ReactNode;
}) {
  const total = ratios.reduce((a, b) => a + b, 0);
  const smallest = Math.min(...ratios);
  const gapsPx = (ratios.length - 1) * gapPx[gapX];
  // The smallest column = (containerW - gaps) * smallest/total; solve for the
  // containerW where that equals minColumnWidth — the point it can no longer
  // hold its content, so the grid drops to one column.
  const collapseBelow =
    minColumnWidth === undefined
      ? undefined
      : Math.round((minColumnWidth * total) / smallest + gapsPx);

  // A wide gutter between rows separates groups that sit side by side; once
  // they're stacked in one column the same gap reads as empty space, so the
  // stacked spacing is capped at `md`. The flex row gap is that stacked
  // spacing, and rows after the first add the rest back as a top margin that
  // only exists while the grid is wide. A percentage margin resolves against
  // the grid's width, which is what lets the same arithmetic switch it off.
  const stackedGapY: GridGap =
    collapseBelow !== undefined && gapPx[gapY] > gapPx.md ? "md" : gapY;
  const extraRowGapPx = gapPx[gapY] - gapPx[stackedGapY];
  const wideRowMargin =
    collapseBelow === undefined || extraRowGapPx === 0
      ? undefined
      : `clamp(0px, calc((100% - ${rem(collapseBelow)}) * 999), ${rem(extraRowGapPx)})`;

  function flexBasis(ratio: number): string {
    // The extra 1px of slack stops sub-pixel rounding from pushing a row's
    // last column onto the next line.
    const share = `calc((100% - ${rem(gapsPx)} - 1px) * ${ratio} / ${total})`;
    if (collapseBelow === undefined) {
      return share;
    }
    // Below the threshold this term is huge, so every child takes a full line
    // (and shrinks to fit it); above, it's negative and the share wins.
    return `max(${share}, calc((${rem(collapseBelow)} - 100%) * 999))`;
  }

  return (
    <div
      className={`flex flex-wrap ${gapXClass[gapX]} ${gapYClass[stackedGapY]}`}
    >
      {Children.toArray(children).map((child, index) => (
        // A one-cell grid so the child stretches to the column's width and
        // the row's height, the way it would as a direct grid item. The
        // minmax(0, …) track holds a wide child to the column instead of
        // letting its content push the column over its neighbour.
        <div
          key={isValidElement(child) ? (child.key ?? index) : index}
          className="grid min-w-0 shrink grow-0 grid-cols-[minmax(0,1fr)]"
          style={{
            flexBasis: flexBasis(ratios[index % ratios.length] ?? smallest),
            ...(index >= ratios.length &&
              wideRowMargin !== undefined && { marginTop: wideRowMargin }),
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
}
